import React, { useMemo, useRef } from 'react';
import { useSelector } from "react-redux";
import moment from 'moment';
import { stringMixin } from "../../utils/strings";
import { useTickerSearch } from "../../modules/Search/TickerSearch/hooks"; // Adjust the path as necessary

const DEFAULT_SYMBOLS = ['spy'];

const getUniqueSymbols = (calendarEvents) => {
    if (!calendarEvents?.length) return [DEFAULT_SYMBOLS, true];

    const symbols = new Set();
    calendarEvents.forEach(x => {
        if (x?.symbol) symbols.add(x.symbol);
        if (Array.isArray(x?.data)) {
            x.data.forEach(y => y?.symbol && symbols.add(y.symbol));
        }
    });

    const uniqueSymbols = Array.from(symbols);
    return uniqueSymbols.length === 0 ? [DEFAULT_SYMBOLS, true] : [uniqueSymbols, false];
};

const useTickerCalendar = () => {
    const prevSymbolsRef = useRef(null);
    const calendar = useSelector((state) => state.user.upcomingEvents);

    const [uniqueSymbols, isDefaultTicker] = useMemo(() => {
        const [symbols, isDefault] = getUniqueSymbols(calendar);
        // Maintain reference equality if symbols haven't changed
        if (prevSymbolsRef.current &&
            symbols.length === prevSymbolsRef.current.length &&
            symbols.every((s, i) => s === prevSymbolsRef.current[i])) {
            return [prevSymbolsRef.current, isDefault];
        }
        prevSymbolsRef.current = symbols;
        return [symbols, isDefault];
    }, [calendar]);

    const { tickers, loading } = useTickerSearch({initialSymbols: uniqueSymbols || []});

    const events = useMemo(() => {
        // Don't process events until ticker search index is fully initialized
        if (loading) {
            return [];
        }

        // Return empty array if there are no calendar events
        if (!calendar?.length) {
            return [];
        }

        const events = [];
        const added = new Set();

        calendar.forEach(x => {
            if (x?.type === 'etf' && Array.isArray(x?.data)) {
                x.data.forEach(y => {
                    if (y?.symbol && !added.has(y.symbol)) {
                        const tickerObject = tickers?.find(t => t.symbol === y.symbol);
                        if (tickerObject) {
                            const isDiv = y.event_type === 'upcomingdividend';
                            const dateField = isDiv ? 'payment_date' : 'report_date';
                            const date = moment(y.data?.[dateField]).toDate();

                            events.push({
                                ...tickerObject,
                                start: date,
                                end: date,
                                title: `$${y.symbol.toUpperCase()} ${stringMixin.fmt(tickerObject?.name ?? '')}: ${y.event_type.replace('upcoming', '')}`,
                                description: isDiv
                                    ? `Expected Dividend: $${y.data?.dollar_value} on ${y.data?.payment_date}. Must be owned by ${y.data?.ex_date}`
                                    : `Earnings Estimate: $${y.data?.estimate_dollar} on ${y.data?.report_date}`,
                                type: y.event_type,
                                symbol: y.symbol,
                                shortDesc: isDiv
                                    ? `Dividend $${y.data?.dollar_value} -- ${y.data?.ex_date}`
                                    : `Est of $ ${y.data?.estimate_dollar} per share`,
                            });
                            added.add(y.symbol);
                        }
                    }
                });
            }
        });

        return events;
    }, [calendar, tickers]); // Added isInitialized to dependencies

    return {
        events,
        uniqueSymbols,
        loading,
    };
};

export default useTickerCalendar;